// Shared page-shell + section + per-block style wrappers.
//
// Before this module, four render surfaces (designer canvas, in-app preview,
// public SSR, standalone runtime) each re-derived page width / padding /
// background and applied per-section color schemes + per-block styles
// differently — so a styled/themed page looked right in the canvas and lost
// its background, content-width, section schemes, and per-block styling on
// every deployed surface. These components are the single implementation
// every surface composes, so they can no longer drift.
//
// `StyledBlock` is now the ONE block wrapper for every off-canvas surface.
// It previously had two hand-duplicated twins — `ServerBlock` in
// frontend/site-ssr/renderSite.tsx and `StyledSizedBlock` in the in-app
// preview's RuntimeBlockHost.tsx — which meant any per-block CSS feature
// (hover states, animation, per-block typography) had to be written three
// times and drifted the moment one was forgotten. Both are now thin
// adapters over this component, so a new wrapper capability lands once.
//
// All components here are pure (no hooks, no window/DataFetcher) so the SSR
// prerenderer (renderToStaticMarkup) can use them too.

import React from 'react'
import type {
  BlockAnimation, BlockSize, BlockStyle, BlockTooltip, ButtonClick,
  Decoration, DesignTokens, ResponsiveStyle, Section, ShapeDivider,
} from '@twasta/page-schema'
import {
  type ResolvedTheme,
  ANIM_CLASS,
  animationAttrs,
  animationClasses,
  animationVars,
  backgroundCss,
  chromeBackgroundCss,
  blockStyleCss,
  blockRulesCss,
  decorationStyle,
  dividerPath,
  hasAnimation,
  hasTooltip,
  needsScopedCss,
  pageParallaxStyle,
  pageRuntimeCss,
  pageRuntimeScript,
  pageChromeScript,
  revealScript,
  sectionLayoutClasses,
  sectionDefaultInset,
  sectionLayoutStyle,
  themeCssVars,
  ensureThemeFontsLoaded,
} from './theme'
import { colorSchemeToCssVars, resolveColorScheme } from './tokens'
import { tw } from './testHooks'
// Re-exported for the static-site renderer, which imports this module by path.
export { tw } from './testHooks'

// ─── ThemeFonts ─────────────────────────────────────────────────────────

/**
 * Loads the theme's webfonts on an interactive surface. The SSR prerenderer
 * writes the `<link>` into the document head itself; on the client nothing
 * did, so a custom Google family (a cloned site's Poppins, say) silently
 * fell back to whatever the host's index.html happened to preload. Effects
 * never run under renderToStaticMarkup, so this stays SSR-safe.
 */
export function ThemeFonts({ theme }: { theme: ResolvedTheme }) {
  React.useEffect(() => { ensureThemeFontsLoaded(theme) }, [theme])
  return null
}

// ─── ThemedPageShell ────────────────────────────────────────────────────

export interface ThemedPageShellProps {
  theme: ResolvedTheme
  children: React.ReactNode
  className?: string
  /** When false, render only the themed outer wrapper and skip the centered,
   *  max-width content column (the caller supplies its own inner layout).
   *  Default true. */
  constrain?: boolean
  /** Merged onto the outer themed wrapper. */
  style?: React.CSSProperties
  /** Merged onto the inner constrained content column. */
  contentStyle?: React.CSSProperties
  /** Outer wrapper min-height. Default '100vh'; pass '100%' inside panes. */
  minHeight?: React.CSSProperties['minHeight']
  /** When true, this shell is a site header/footer CHROME bar: use the
   *  palette background as the bar color and ignore a default solid-white
   *  page `layout.background` that would otherwise clobber a dark header.
   *  Gradient/image layout backgrounds are still honored. */
  chrome?: boolean
}

/**
 * Themed page wrapper. Emits the full `--pg-*` variable set via themeCssVars
 * and paints the page background (solid base + optional gradient/image
 * layer), then — unless `constrain` is false — centers a max-width content
 * column whose width/padding/gap come from the same variables.
 *
 * It also injects the page-runtime stylesheet (`pageRuntimeCss()`) once:
 * animation keyframes, section layout, tooltips, marquee and page chrome.
 * Every rule in there is inert until a block opts in, so the cost to a page
 * that uses none of it is a single small <style> tag.
 */
export function ThemedPageShell({
  theme,
  children,
  className,
  constrain = true,
  style,
  contentStyle,
  minHeight = '100vh',
  chrome = false,
}: ThemedPageShellProps) {
  const outerStyle: React.CSSProperties = {
    ...(themeCssVars(theme) as React.CSSProperties),
    // Solid base first; backgroundCss() then either overrides `background`
    // (color kind) or layers a gradient/image on top while this stays the
    // fallback underneath (gradient/image kinds return backgroundImage only).
    // For a chrome bar, a default solid-white layout background must NOT
    // override the palette background (which is the bar's intended color).
    ...(chrome
      ? chromeBackgroundCss(theme)
      : { background: theme.palette.background, ...backgroundCss(theme.layout.background), ...pageParallaxStyle(theme.layout.background) }),
    color: 'var(--pg-text)',
    fontFamily: 'var(--pg-font, system-ui, sans-serif)',
    minHeight,
    ...style,
  }

  // `pg-themed` is the hook the dark-mode rules re-declare their palette on,
  // so a mode switch reaches every nested block through the same variables.
  const cls = ['pg-themed', className].filter(Boolean).join(' ')

  if (!constrain) {
    return (
      <div className={cls} style={outerStyle}>
        <style dangerouslySetInnerHTML={{ __html: pageRuntimeCss() }} />
        <ThemeFonts theme={theme} />
        <PageRuntimeScripts />
        {children}
      </div>
    )
  }

  return (
    <div className={cls} style={outerStyle}>
      <style dangerouslySetInnerHTML={{ __html: pageRuntimeCss() }} />
      <ThemeFonts theme={theme} />
      <PageRuntimeScripts />
      <div
        style={{
          maxWidth: 'var(--pg-content-width)',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
          padding: 'var(--pg-page-padding-y) var(--pg-page-padding-x)',
          display: 'grid',
          // Cap the single implicit column at the container width. Without
          // `minmax(0, …)` a grid column's minimum is `min-content`, so a wide
          // child (e.g. an embedded transaction table once its data loads)
          // expands the track past `--pg-content-width` and the whole page
          // scrolls horizontally. `minmax(0, 1fr)` lets the column shrink to 0
          // so wide content scrolls INSIDE its own overflow box instead.
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 'var(--pg-block-gap)',
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * Boots the shared vanilla page-runtime behaviour on an INTERACTIVE surface.
 *
 * The static site inlines these scripts as `<script>` tags in its document;
 * the React surfaces have no such document to write into, so they evaluate
 * the same strings once on mount. Using the same implementation on both
 * sides is the point — a React-only version of, say, the reveal observer
 * would silently drift from the one the published site actually runs.
 *
 * Every script guards against double-execution, so mounting several shells
 * (the designer preview inside the workbench, for instance) is harmless.
 * Renders nothing, and does nothing during SSR (effects don't run there).
 */
export function PageRuntimeScripts() {
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    for (const src of [pageRuntimeScript(), revealScript(), pageChromeScript()]) {
      try {
        // eslint-disable-next-line no-new-func
        new Function(src)()
      } catch (err) {
        // A failure here must never take the page down: everything these
        // scripts drive is an enhancement over already-rendered content.
        console.error('[page-runtime] behaviour script failed to start', err)
      }
    }
  }, [])
  return null
}

// ─── Shape dividers & decorations ───────────────────────────────────────

/**
 * One SVG shape divider on a section edge. Purely presentational, so it is
 * `aria-hidden` and never intercepts pointer events; a browser that fails to
 * render it simply leaves a straight edge, which is still a valid page.
 */
export function SectionDivider({ divider, edge }: { divider: ShapeDivider; edge: 'top' | 'bottom' }) {
  const path = dividerPath(divider.kind)
  if (!path) return null
  const height = divider.height ?? 60
  // A top divider is the same path flipped vertically, so one path table
  // serves both edges.
  const flips: string[] = []
  if (edge === 'top') flips.push('scaleY(-1)')
  if (divider.flipX) flips.push('scaleX(-1)')
  return (
    <div
      aria-hidden
      className={['pg-div', `pg-div-${edge}`, divider.above ? 'pg-div-above' : ''].filter(Boolean).join(' ')}
      style={{ height, transform: flips.join(' ') || undefined }}
    >
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ height }}>
        <path d={path} fill={divider.color || 'var(--pg-bg)'} />
      </svg>
    </div>
  )
}

/** The decorative layers (blob / glow / dot-grid / noise / rings) of a section. */
export function SectionDecorations({ decorations }: { decorations: Decoration[] | undefined }) {
  if (!decorations || !decorations.length) return null
  return (
    <>
      {decorations.map((d) => (
        <div key={d.id} aria-hidden className={`pg-deco pg-deco-${d.kind}`} style={decorationStyle(d)} />
      ))}
    </>
  )
}

// ─── SectionWrapper ─────────────────────────────────────────────────────

export interface SectionWrapperProps {
  section: Section
  designTokens?: DesignTokens
  children: React.ReactNode
}

const SEMANTIC_TAGS: ReadonlySet<string> = new Set(['header', 'main', 'footer', 'aside'])

/**
 * Wraps a section's blocks, applying its color-scheme override (as `--pg-*`
 * variables so nested blocks inherit it), its per-section style, its
 * block-gap override, and the Tier-3 layout primitives — full-bleed /
 * breakout width, viewport-height with vertical alignment, shape dividers,
 * decorative layers and sticky pinning.
 *
 * The width primitives matter more than they look: every section used to be
 * a fixed-width column inside one vertical stack, which is the single
 * biggest reason stock output reads as templated. `width: 'full'` lets a
 * band's background bleed edge-to-edge while its content stays on the same
 * measure as every other section.
 */
export function SectionWrapper({ section, designTokens, children }: SectionWrapperProps) {
  const scheme = resolveColorScheme(designTokens, section.colorSchemeId)
  const isFull = (section.width || 'contained') === 'full'
  const style: React.CSSProperties = {
    ...(scheme ? (colorSchemeToCssVars(scheme) as React.CSSProperties) : {}),
    ...blockStyleCss(section.style),
    // A full-bleed band already insets its content (`.pg-sec-inner`); any
    // other section that paints a surface gets a default side inset.
    ...(isFull ? {} : sectionDefaultInset(section, !!scheme)),
    ...sectionLayoutStyle(section),
  }
  if (scheme) {
    // Paint the section surface so an opted-in scheme is visible even when
    // its blocks are transparent — unless the section paints its OWN
    // background (a photo band under a veil, a gradient): the scheme then
    // supplies the text/link colours for that surface and must not cover it.
    // Same precedence as the static site's ServerSection.
    const ownBg = section.style?.background
    if (!ownBg || (ownBg as { kind?: string }).kind === 'none') style.background = scheme.background
    style.color = scheme.text
  }
  if (typeof section.blockGap === 'number') {
    ;(style as Record<string, string>)['--pg-block-gap'] = `${section.blockGap}px`
  }

  const stackStyle: React.CSSProperties = {
    display: 'grid',
    // Same grid-blowout guard as the page content column: cap the column at
    // the container width so a wide block scrolls internally, not the page.
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 'var(--pg-block-gap)',
  }

  const Tag = (section.tag && SEMANTIC_TAGS.has(section.tag) ? section.tag : 'section') as 'section'
  const sectionHook = tw('block', `section:${section.id || ''}`, { name: section.name })
  const classes = sectionLayoutClasses(section)
  const hasChrome = !!(section.dividerTop || section.dividerBottom || section.decorations?.length)

  // A full-bleed section needs an inner element to re-constrain its content;
  // for every other width the stack IS the section body.
  const body = isFull
    ? <div className="pg-sec-inner" style={stackStyle}>{children}</div>
    : <div className="pg-sec-body" style={stackStyle}>{children}</div>

  if (!hasChrome && classes.length === 1 && !isFull) {
    // Plain section — keep the original flat markup so nothing regresses for
    // the overwhelmingly common case. The anchor id still rides along: it
    // has to land on a real element or the section is not a scroll target.
    return <Tag id={section.anchorId || undefined} {...sectionHook} className="pg-sec" style={{ ...style, ...stackStyle }}>{children}</Tag>
  }

  return (
    <Tag id={section.anchorId || undefined} {...sectionHook} className={classes.join(' ')} style={style}>
      <SectionDecorations decorations={section.decorations} />
      {section.dividerTop ? <SectionDivider divider={section.dividerTop} edge="top" /> : null}
      {body}
      {section.dividerBottom ? <SectionDivider divider={section.dividerBottom} edge="bottom" /> : null}
    </Tag>
  )
}

// ─── StyledBlock ────────────────────────────────────────────────────────

export interface StyledBlockProps {
  style?: BlockStyle
  // Per-breakpoint overrides + the block id used to scope the emitted
  // @media / :hover rules. Both are required together for scoped output.
  responsive?: ResponsiveStyle
  blockId?: string
  /**
   * The block's `type`. Used only to resolve an UNSET `style.hoverScope`:
   * a block that renders repeated elements defaults to per-item hover, so a
   * card grid lifts the card under the cursor rather than the whole grid.
   */
  blockType?: string
  /** Explicit width/height from the Size & Position panel. */
  size?: BlockSize
  /** Entrance motion. */
  animation?: BlockAnimation
  /** `scroll_to` / `#hash` target — emitted as the wrapper's `id`. */
  anchorId?: string
  /** Hover/focus tooltip. */
  tooltip?: BlockTooltip
  /** Whole-block click intent. `onActivate` is supplied by the surface. */
  on_click?: ButtonClick
  onActivate?: (click: ButtonClick) => void
  /** Extra classes the surface wants on the wrapper. */
  className?: string
  children: React.ReactNode
}

/** Convert a BlockDimension ({ value, unit }) to a CSS length string. */
export function dimToCss(d: { value: number; unit: string } | undefined): string | undefined {
  if (!d || typeof d.value !== 'number' || !Number.isFinite(d.value)) return undefined
  return `${d.value}${d.unit}`
}

/**
 * Blocks whose content FLOWS (text that wraps, a menu row that wraps, a
 * columns row holding them). Their authored height is a MINIMUM: a longer
 * site title or one more menu item makes the bar taller instead of spilling
 * out over the next band. Media and fixed boxes keep an exact height.
 */
const GROW_TO_CONTENT_BLOCKS = new Set(['columns', 'nav_menu', 'auth_menu', 'heading', 'paragraph', 'rich_content'])

/** The CSS property a block's authored height goes on. */
export function blockHeightProp(blockType: string | undefined): 'height' | 'minHeight' {
  return blockType && GROW_TO_CONTENT_BLOCKS.has(blockType) ? 'minHeight' : 'height'
}

/**
 * THE per-block wrapper for every off-canvas surface.
 *
 * Responsibilities, in the order they compose:
 *   • per-block style (background / border / radius / shadow / padding /
 *     margin / align / textColor / typography / layering)
 *   • per-breakpoint overrides and `:hover` / `:focus-within` states
 *   • entrance animation (class + custom properties + reveal hook)
 *   • explicit size, anchor id, tooltip, and whole-block click intent
 *
 * Rendering strategy: a block that needs nothing renders its children
 * UNWRAPPED, so the DOM for a plain paragraph is byte-identical to before.
 * A block that only needs inline-expressible style gets one <div> with an
 * inline style. Only a block carrying responsive layers or interaction
 * states falls back to the scoped-<style> path — inline styles can't express
 * `@media`/`:hover`, and inline specificity would also outrank the overrides.
 */
/**
 * The id of the block copy a REPEAT GRID cell holds, or null outside one.
 *
 * A repeat laid out as a grid stretches every cell to the row's height,
 * but the wrappers between the cell and the block (this one, a static
 * page's block link) were auto-height, so a repeated card stopped at its
 * own content and a row of products had ragged bottoms and prices at
 * different heights. Each surface's repeat provides the copy's id here
 * (`provideRepeatCell`), and anything rendered FOR that copy — this
 * wrapper, the card itself — fills the cell. Keyed by id rather than a
 * boolean so it never leaks into blocks NESTED inside the copy.
 */
export const RepeatCellContext = React.createContext<string | null>(null)

/** True when `blockId` is the copy a repeat-grid cell holds. */
export function useRepeatCell(blockId: string | undefined): boolean {
  const cell = React.useContext(RepeatCellContext)
  return !!blockId && cell === blockId
}

/**
 * Wrap one repeated copy. `grid` is whether the repeat lays its copies out
 * in a grid (`repeatLayoutStyle` returned a style); a flow repeat provides
 * nothing, so a stacked list keeps each block's natural size.
 */
export function provideRepeatCell(grid: boolean, copyId: string, node: React.ReactNode): React.ReactNode {
  if (!grid) return node
  return <RepeatCellContext.Provider value={copyId}>{node}</RepeatCellContext.Provider>
}

export function StyledBlock({
  style, responsive, blockId, blockType, size, animation, anchorId, tooltip,
  on_click, onActivate, className, children,
}: StyledBlockProps) {
  const inCell = useRepeatCell(blockId)
  // ── Size ──
  const width = dimToCss(size?.width)
  const height = dimToCss(size?.height)
  const sizeStyle: React.CSSProperties = {}
  if (width) { sizeStyle.width = width; sizeStyle.maxWidth = '100%' }
  // Set the height but keep overflow visible: a fixed-height block (e.g. a
  // header bar) must NOT clip absolutely-positioned descendants like nav
  // dropdowns, and must not introduce a scrollbar.
  if (height) sizeStyle[blockHeightProp(blockType)] = height
  const hasSize = !!(width || height)
  // A repeat-grid cell: when a wrapper is drawn at all, fill the cell so
  // the block inside can (see RepeatCellContext). No wrapper is ever added
  // for this alone — a bare block is already the stretched grid item.
  const cellFill: React.CSSProperties = inCell && !height ? { height: '100%' } : {}

  // ── Motion ──
  const animCls = animationClasses(animation)
  const animAttrs = animationAttrs(animation)
  const animStyle = animationVars(animation)

  // ── Click intent ──
  const clickable = !!(on_click && on_click.kind !== 'none' && onActivate)
  const clickProps: Record<string, unknown> = clickable
    ? {
        role: on_click!.kind === 'external_url' || on_click!.kind === 'navigate' ? 'link' : 'button',
        tabIndex: 0,
        onClick: (e: React.MouseEvent) => {
          // A real <a>/<button> inside the block keeps its own behaviour —
          // only clicks on the block's own surface trigger the block intent.
          if ((e.target as HTMLElement).closest('a,button,input,select,textarea')) return
          onActivate!(on_click!)
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate!(on_click!) }
        },
        style: { cursor: 'pointer' },
      }
    : {}

  // ── Tooltip ──
  const tip = hasTooltip(tooltip) ? tooltip! : undefined
  const tipNode = tip
    ? (
        <span className="pg-tip-bub" role="tooltip">
          <b>{tip.title}</b>
          {tip.body ? tip.body : null}
        </span>
      )
    : null

  const classes = [
    className,
    ...animCls,
    tip ? 'pg-tip' : '',
  ].filter(Boolean)

  // Test hook: every block a host renders is addressable as `<type>:<id>`.
  const hook = blockId ? tw('block', `${blockType || 'block'}:${blockId}`) : {}
  const extraAttrs: Record<string, unknown> = {
    ...hook,
    ...animAttrs,
    ...(tip ? { 'data-tip-place': tip.placement || 'top' } : {}),
    ...(anchorId ? { id: anchorId } : {}),
  }

  const scoped = needsScopedCss(style, responsive) && !!blockId
  const wrapperNeeded =
    scoped || hasSize || classes.length > 0 || !!anchorId || clickable ||
    hasAnimation(animation) || Object.keys(blockStyleCss(style, blockType)).length > 0

  // No wrapper of its own: a `display: contents` element carries the test hook
  // without adding a box, so layout is exactly what it was without it.
  if (!wrapperNeeded) {
    return blockId
      ? <div {...hook} style={{ display: 'contents' }}>{children}</div>
      : <>{children}</>
  }

  if (scoped) {
    const cls = `pg-b-${blockId}`
    const rules = blockRulesCss(cls, style, responsive, blockType)
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: rules }} />
        <div
          className={[cls, ...classes].join(' ')}
          {...extraAttrs}
          {...clickProps}
          style={{
            ...(hasSize ? { ...sizeStyle, boxSizing: 'border-box' } : {}),
            ...cellFill,
            ...animStyle,
            ...(clickProps.style as React.CSSProperties | undefined),
          }}
        >
          {children}
          {tipNode}
        </div>
      </>
    )
  }

  const css = blockStyleCss(style, blockType)
  return (
    <div
      className={classes.join(' ') || undefined}
      {...extraAttrs}
      {...clickProps}
      style={{
        ...css,
        ...(hasSize ? { ...sizeStyle, boxSizing: 'border-box' } : {}),
        ...cellFill,
        ...animStyle,
        ...(clickProps.style as React.CSSProperties | undefined),
      }}
    >
      {children}
      {tipNode}
    </div>
  )
}

// Re-export so surfaces can render the animation class name without
// importing from two modules.
export { ANIM_CLASS }
